import axios from "axios";
import type { AxiosError } from "axios";
import type { Reference, DrugSearchResult } from "common/models/types";

/**
 * 케어링노트 파트너 열람 전용 API 클라이언트 (계획 §3-2 · T1).
 *
 * ⚠️ 인터셉터를 붙이지 않는다 — 기존 `axiosInstance`/`ymydApi`의 401 리다이렉트가 걸리면
 *    파트너 경로에서 로그인 화면으로 튕긴다(계획 성공기준 7 위반). 기존 클라이언트를 재사용·수정하지 않는다.
 * ⚠️ `withCredentials: false` — URL 토큰이 유일한 인증. 서비스 쿠키(sameSite strict)를 실어 보내지 않는다.
 * ⚠️ 조회 전용 — 쓰기 함수가 없다(서버에도 쓰기 경로가 없다).
 *
 * baseURL은 `axiosInstance`와 동일 백엔드다(운영: `REACT_APP_BACKEND_URL`, dev: 프록시로 빈 문자열).
 */
const BASE_URL =
  process.env.NODE_ENV === "production" ? process.env.REACT_APP_BACKEND_URL || "" : "";

export const caringnoteApiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  withCredentials: false,
});

/** embed 응답 — 복약지도문 위주(근거문헌 없음). */
export interface EmbedReport {
  reviewId: number;
  completedAt: string | null;
  content: string;
  /**
   * 렌더 분기용 프롬프트 타입. 프론트가 하드코딩하면 백엔드 분석 프리셋과 갈린다(2026-08-14).
   * 부재 시 'medication_counseling'로 폴백(toPartnerReview와 동일 규약).
   */
  promptType?: string | null;
  /**
   * 편집본(3단 DOM 스냅샷 HTML) 탑재 여부. 참이면 3단과 동일하게 HtmlViewer, 거짓이면 MarkdownViewer 경로.
   * ⚠️ 계약상 non-optional boolean이나, Phase 1(백엔드) 미배포 전이 기간의 필드 부재를 모델링하려 optional로 둔다.
   *    값이 존재할 때의 타입은 계약과 동일. Phase 1 실배포 후 `?` 제거(계획 §10 C-3).
   */
  isEdited?: boolean;
  /**
   * 본문에 언급된 병원 alias→실명 매핑(spec 설계 결정 8 · A안). 임베드는 스냅샷을 담지 않으므로
   * 실명 복원 재료를 이 필드로 받는다(referenceMode='none'에서 실명 평문 표기).
   * ⚠️ 백엔드 필드는 별도 세션이 병렬 구현 중 — 프론트는 optional로 방어(부재 시 ⑆ 벗긴 평문 폴백).
   */
  hospitalAliases?: Record<string, string>;
  /**
   * 환자 스냅샷 — 나이·성별·질환·알레르기·상담내용·처방/OTC/건기식 배열. 내부 식별자(id·userId)는 서버가 제거해 보낸다.
   * ⚠️ 탑재뷰(embed) 응답에 실린다(백엔드 Phase 1, `feat/caringnote-embed-patientsnap`). dev 미배포 전이기엔
   *    필드가 아예 없거나 null일 수 있으므로 optional. 탑재뷰는 부재 시 환자정보·처방표를 비활성 처리한다(계획 §5.5).
   */
  patientSnap?: any;
  /**
   * 본문 약 카드의 성상(chart)·각인(print) 소스 — `DrugSearchResult[]`(무가공, pills.json 구조 그대로).
   * `DrugImage`가 `itemSeq === itemCode`로 매칭해 성상·각인 줄을 채운다(계획 D-4 · 파생일 2026-09-01).
   * ⚠️ 백엔드 Phase 1(`02e26b5`, 미푸시)이 최상위로 실어 보낸다. dev 미배포 전이기엔 부재 가능 → optional(D-10).
   *    **값을 가공하지 마라** — trim·따옴표 제거·키 변경·숫자 변환 시 매칭이 조용히 실패한다(D-4).
   */
  pills?: DrugSearchResult[];
}

/** report 응답 — 근거문헌 포함. `promptType`·`patientSnap`은 EmbedReport에서 상속. */
export interface FullReport extends EmbedReport {
  references: Reference | null;
}

/**
 * 열람 오류를 화면 상태로 분류한다. **어떤 분기도 로그인 유도를 만들지 않는다**(계획 §3-3).
 * - 응답 없음(네트워크·타임아웃) → `network` (재시도 허용)
 * - 404(없는·폐기 토큰) → `notFound`
 * - 403(용도 교차) → `scopeMismatch` (`TOKEN_SCOPE_MISMATCH`)
 * - 그 외(5xx·예상 밖 상태 포함) → `server` (재시도 허용) — 401이 와도 로그인으로 보내지 않는다.
 */
export type CaringNoteErrorKind = "notFound" | "scopeMismatch" | "server" | "network";

export interface CaringNoteError {
  kind: CaringNoteErrorKind;
  /** 평면 오류봉투의 코드(있으면). 예: TOKEN_SCOPE_MISMATCH */
  code?: string;
}

/** 평면 오류봉투 `{ error: { code, message } }`에서 code만 안전 추출(내부 봉투 아님). */
function extractErrorCode(data: unknown): string | undefined {
  if (data && typeof data === "object" && "error" in data) {
    const err = (data as { error?: unknown }).error;
    if (err && typeof err === "object" && "code" in err) {
      const code = (err as { code?: unknown }).code;
      if (typeof code === "string") return code;
    }
  }
  return undefined;
}

export function classifyCaringNoteError(error: unknown): CaringNoteError {
  const response = (error as AxiosError | undefined)?.response;
  if (!response) {
    // 네트워크 오류·타임아웃(ECONNABORTED)·CORS 등 — 응답 자체가 없다.
    return { kind: "network" };
  }
  const code = extractErrorCode(response.data);
  if (response.status === 404) return { kind: "notFound", code };
  if (response.status === 403) return { kind: "scopeMismatch", code };
  return { kind: "server", code };
}

const EMBED_PATH = "/api/v1/partner/caringnote/reports/embed";
const REPORT_PATH = "/api/v1/partner/caringnote/reports/report";

/** 탑재뷰(embed) 열람 — 복약지도문만. */
export async function fetchEmbedReport(token: string): Promise<EmbedReport> {
  const res = await caringnoteApiClient.get<EmbedReport>(
    `${EMBED_PATH}/${encodeURIComponent(token)}`
  );
  return res.data;
}

/** 독립뷰(report) 열람 — 복약지도문 + 근거문헌. */
export async function fetchFullReport(token: string): Promise<FullReport> {
  const res = await caringnoteApiClient.get<FullReport>(
    `${REPORT_PATH}/${encodeURIComponent(token)}`
  );
  return res.data;
}
